import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Col, DatePicker, Row, Statistic, Typography } from "antd";
import dayjs, { Dayjs } from "dayjs";
import type { ImbalancePriceInsight } from "../../../services/client";
import { getImbalancePriceInsightsOptions } from "../../../services/client/@tanstack/react-query.gen";
import { ONE_MINUTE } from "../../../util/time";
import { adminAuthHeaders } from "../adminAuthHeaders";
import { ImbalanceForecastChart } from "./ImbalanceForecastChart";
import { ImbalanceSpreadChart } from "./ImbalanceSpreadChart";
import { sideScores, spreadRows } from "./imbalanceSpread";
import { useDayAheadPrices } from "./useDayAheadPrices";
import { DATE_FORMAT } from "../../../util/formatDate";

const { RangePicker } = DatePicker;
const { Text } = Typography;

const lastMinuteForecastErrors = (insights: ImbalancePriceInsight[]) =>
  insights.flatMap(({ actual_price, latest_minute_forecast }) =>
    actual_price === null ||
    latest_minute_forecast === null ||
    latest_minute_forecast === undefined
      ? []
      : [Math.abs(actual_price - latest_minute_forecast)],
  );

// How often the final price fell inside, below or above a percentile band of
// the per-minute forecast, over every minute forecast that carries the band.
// A well-calibrated 10-90 band holds the final price about 80% of the time.
type BandCalibration = {
  compared: number;
  insideShare: number;
  belowShare: number;
  aboveShare: number;
};

const bandCalibration = (
  insights: ImbalancePriceInsight[],
  low: string,
  high: string,
): BandCalibration => {
  let compared = 0;
  let below = 0;
  let above = 0;
  insights.forEach(({ actual_price, minute_forecasts }) => {
    if (actual_price === null || !minute_forecasts) {
      return;
    }
    minute_forecasts.forEach(({ predicted_price_quantiles }) => {
      const lowValue = predicted_price_quantiles?.[low];
      const highValue = predicted_price_quantiles?.[high];
      if (lowValue === undefined || highValue === undefined) {
        return;
      }
      compared += 1;
      if (actual_price < lowValue) {
        below += 1;
      } else if (actual_price > highValue) {
        above += 1;
      }
    });
  });
  return {
    compared,
    insideShare: compared === 0 ? 0 : (compared - below - above) / compared,
    belowShare: compared === 0 ? 0 : below / compared,
    aboveShare: compared === 0 ? 0 : above / compared,
  };
};

const meanOf = (values: number[]) =>
  values.reduce((sum, value) => sum + value, 0) / values.length;

const latestForecastErrors = (insights: ImbalancePriceInsight[]) =>
  insights.flatMap(({ actual_price, latest_prediction }) =>
    actual_price === null || latest_prediction === null
      ? []
      : [Math.abs(actual_price - latest_prediction)],
  );

export const ImbalancePrices = () => {
  const [range, setRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(1, "day"),
    dayjs(),
  ]);

  const { data, isLoading, error } = useQuery({
    ...getImbalancePriceInsightsOptions({
      headers: adminAuthHeaders(),
      query: {
        start: range[0].startOf("day").toISOString(),
        end: range[1].add(1, "day").startOf("day").toISOString(),
      },
    }),
    retry: false,
    staleTime: 5 * ONE_MINUTE,
  });
  const insights = useMemo(() => data ?? [], [data]);
  const errors = useMemo(() => latestForecastErrors(insights), [insights]);
  const minuteErrors = useMemo(
    () => lastMinuteForecastErrors(insights),
    [insights],
  );
  const wideBand = useMemo(
    () => bandCalibration(insights, "0.1", "0.9"),
    [insights],
  );
  const narrowBand = useMemo(
    () => bandCalibration(insights, "0.25", "0.75"),
    [insights],
  );
  const dayAhead = useDayAheadPrices("BE", range[0], range[1]);
  const rows = useMemo(
    () => spreadRows(insights, dayAhead.actual),
    [insights, dayAhead.actual],
  );
  const scores = useMemo(() => sideScores(rows), [rows]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <RangePicker
          format={DATE_FORMAT}
          value={range}
          allowClear={false}
          onChange={(value) => {
            if (value?.[0] && value?.[1]) {
              setRange([value[0], value[1]]);
            }
          }}
        />
        <Text type="secondary">
          Imbalance prices published by Elia, against our forecasts. Each band
          spans the prices we predicted for a quarter hour within the last 24h,
          12h, 4h and 1h before it started. The purple line shows the forecast
          refreshed every minute while a quarter hour is running, and the purple
          bands the 10-90 and 25-75 percentile ranges it gives for the final
          price.
        </Text>
      </div>

      {(errors.length > 0 || minuteErrors.length > 0) && (
        <Row gutter={32} align="middle">
          {errors.length > 0 && (
            <>
              <Col>
                <Statistic
                  title="Mean absolute error of the latest forecast"
                  value={meanOf(errors)}
                  precision={2}
                  suffix="€/MWh"
                />
              </Col>
              <Col>
                <Statistic
                  title="Quarter hours compared"
                  value={errors.length}
                />
              </Col>
            </>
          )}
          {minuteErrors.length > 0 && (
            <>
              <Col>
                <Statistic
                  title="Mean absolute error of the last per-minute forecast"
                  value={meanOf(minuteErrors)}
                  precision={2}
                  suffix="€/MWh"
                />
              </Col>
              <Col>
                <Statistic
                  title="Quarter hours compared"
                  value={minuteErrors.length}
                />
              </Col>
            </>
          )}
        </Row>
      )}

      {wideBand.compared > 0 && (
        <Row gutter={32} align="middle">
          <Col>
            <Statistic
              title="Final price inside the 10-90 band"
              value={wideBand.insideShare * 100}
              precision={1}
              suffix="%"
            />
          </Col>
          <Col>
            <Statistic
              title="Below the 10th percentile"
              value={wideBand.belowShare * 100}
              precision={1}
              suffix="%"
            />
          </Col>
          <Col>
            <Statistic
              title="Above the 90th percentile"
              value={wideBand.aboveShare * 100}
              precision={1}
              suffix="%"
            />
          </Col>
          <Col>
            <Statistic
              title="Inside the 25-75 band"
              value={narrowBand.insideShare * 100}
              precision={1}
              suffix="%"
            />
          </Col>
          <Col>
            <Statistic
              title="Minute forecasts compared"
              value={wideBand.compared}
            />
          </Col>
        </Row>
      )}

      <ImbalanceForecastChart
        insights={insights}
        isLoading={isLoading}
        error={error}
      />

      <Text type="secondary">
        Our nomination is bought at the day-ahead price, and every deviation
        from it is settled at the imbalance price. When the imbalance price ends
        up above day-ahead, injecting more than nominated earns the difference;
        below it, taking off more does. Red quarter hours are where our latest
        forecast put the imbalance price on the wrong side of day-ahead.
      </Text>

      {scores.quarterHours > 0 && (
        <Row gutter={32} align="middle">
          <Col>
            <Statistic
              title="Right side of day-ahead"
              value={scores.rightShare * 100}
              precision={1}
              suffix="%"
            />
          </Col>
          <Col>
            <Statistic
              title="Share of the spread (€) on the right side"
              value={scores.rightWorthShare * 100}
              precision={1}
              suffix="%"
            />
          </Col>
          <Col>
            <Statistic
              title="Quarter hours compared"
              value={scores.quarterHours}
            />
          </Col>
        </Row>
      )}

      <ImbalanceSpreadChart
        insights={insights}
        rows={rows}
        isLoading={isLoading || dayAhead.isLoading}
        error={error ?? dayAhead.error}
      />
    </div>
  );
};
